import { AreaChart } from "@mantine/charts";
import { Card, ColorSwatch, Divider, Group, Paper, Skeleton, Stack, Text } from "@mantine/core";
import { IconChartAreaLine } from "@tabler/icons-react";
import type { DashboardData } from "../../../apis/myDashboard";
import { formatAmount, formatMoney } from "../../../utils/format";
import { EmptyState } from "../EmptyState";
import { MoneyCell } from "../MoneyCell";
import { SectionHeader } from "../SectionHeader";

interface Props {
  data?: DashboardData;
  loading: boolean;
  currency: string;
}

// "2026-06" -> "6 сар"
const monthLabel = (ym: string): string => {
  const m = Number(ym.split("-")[1]);
  return Number.isFinite(m) ? `${m} сар` : ym;
};

const SERIES = [
  { name: "collected", label: "Цуглуулсан шимтгэл", color: "teal.6" },
  { name: "platformCost", label: "Платформын зардал", color: "orange.6" }
];

// Custom tooltip adds the per-month NET (collected − cost) the default one can't show.
// recharts payload typing is loose, so this stays defensive.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function TrendTooltip({ label, payload, currency }: { label?: any; payload?: readonly any[]; currency: string }) {
  if (!payload || payload.length === 0) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const valueOf = (name: string): number => Number(payload.find((p: any) => p.name === name)?.value ?? 0);
  const collected = valueOf("collected");
  const cost = valueOf("platformCost");
  const net = collected - cost;
  return (
    <Paper withBorder shadow="sm" radius="md" p="sm" miw={200}>
      <Text size="xs" fw={600} mb={6}>{label}</Text>
      <Stack gap={6}>
        {SERIES.map((s) => (
          <Group key={s.name} justify="space-between" wrap="nowrap" gap="lg">
            <Group gap={6} wrap="nowrap">
              <ColorSwatch color={`var(--mantine-color-${s.color.replace(".", "-")})`} size={10} withShadow={false} />
              <Text size="xs" c="dimmed">{s.label}</Text>
            </Group>
            <MoneyCell value={s.name === "collected" ? collected : cost} currency={currency} size="xs" fw={600} />
          </Group>
        ))}
        <Divider my={2} />
        <Group justify="space-between" wrap="nowrap" gap="lg">
          <Text size="xs" fw={600}>Цэвэр</Text>
          <Text span size="xs" fw={700} c={net >= 0 ? "green" : "red"}>{formatMoney(net, currency)}</Text>
        </Group>
      </Stack>
    </Paper>
  );
}

// ROW 2 — revenue vs platform-cost over 6/12 months (UI-SPEC §4.1).
export function RevenueTrendChart({ data, loading, currency }: Props) {
  const trend = data?.revenueTrend ?? [];
  const monthsWithData = trend.filter((d) => Number(d.collected) > 0 || Number(d.platformCost) > 0).length;
  const chartData = trend.map((d) => ({
    month       : monthLabel(d.month),
    collected   : Number(d.collected),
    platformCost: Number(d.platformCost)
  }));

  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm" h="100%">
        <SectionHeader title="Орлого vs Платформын зардал" eyebrow={`Сүүлийн ${trend.length || 6} сар`} />
        {loading ? (
          <Skeleton height={260} radius="sm" />
        ) : monthsWithData < 3 ? (
          <EmptyState
            icon={<IconChartAreaLine size={26} />}
            message="Хангалттай түүх алга — эхний нэхэмжлэхийн дараа график үүснэ."
          />
        ) : (
          <AreaChart
            h={260}
            data={chartData}
            dataKey="month"
            type="default"
            withLegend
            curveType="monotone"
            withDots={false}
            fillOpacity={0.18}
            strokeWidth={2}
            valueFormatter={(value) => formatAmount(value)}
            series={SERIES}
            tooltipProps={{
              content: ({ label, payload }) => <TrendTooltip label={label} payload={payload} currency={currency} />
            }}
          />
        )}
      </Stack>
    </Card>
  );
}
